/**
 * Created by ivo on 12.05.17.
 */

import React from 'react';
import PropTypes from 'prop-types';

import BaseContainer from '../../containers/BaseContainer';
import LoginPage from './LoginPage';
import {SessionStore, SessionActions} from '../../services/Session';
import SelectRole from "../../modals/SelectRole";
import Navigation from "../../Navigation";
import {NotificationActions} from "../../services/Notification";
import ResetPassword from "../../modals/ResetPassword";
import ForgotPassword from "../../modals/ForgotPassword";
import {PostRestAction} from "../../services/RestAction";
import {queryParse} from "../../util";

/**
 * Implements OAuth2-based authentication process:
 * - User authentication (both password and TOTP)
 * - Password change if required
 * - TOTP issuing if required
 * - Role selection if required
 * - Redirect back to caller URL
 */
class AuthContainer extends BaseContainer {
    static propTypes = {
        location: PropTypes.shape({
            query: PropTypes.shape({
                /**
                 * Application code
                 */
                client_id: PropTypes.string.isRequired,
                /**
                 * Application redirect URL
                 */
                redirect_url: PropTypes.string.isRequired,
                /**
                 * Random state string
                 */
                state: PropTypes.string,
                /**
                 * Authentication scope
                 */
                scope: PropTypes.string
            }).isRequired,
            state: PropTypes.shape({
                redirect: PropTypes.string
            })
        }).isRequired
    };

    static defaultProps = {};

    constructor(props) {
        super(props, {
            session: SessionStore
        });
        this.state.type = this.state.session.passwordTab;
        this.state.username = "";
        this.state.password = "";
        this.state.code = "";
        this.state.modal = "";

        this.onSubmitPassword = this.onSubmitPassword.bind(this);
        this.onForgotPassword = this.onForgotPassword.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onCloseForgotModal = this.onCloseForgotModal.bind(this);
        this.onCloseRoleModal = this.onCloseRoleModal.bind(this);
        this.onClosePasswordChange = this.onClosePasswordChange.bind(this);
    }

    /**
     * Submits password to the server
     * @param {string} type Authentication type: "authcode" - two-factor authentication, "password" - password authentication
     * @param {string} username Username
     * @param {string} password Password
     * @param {string?} code Authentication code (only in case of two-factor authentication)
     * @param {string?} newPassword New Password (only in case of password change)
     */
    onSubmitPassword(type, username, password, code = null, newPassword = null) {
        this.setState({
            type: type,
            username: username,
            password: password,
            code: code,
            newPassword: newPassword
        }, function () {
            let authRequest = {
                "person": {
                    "email": username,
                    "password": password,
                    "authCode": (type == "authcode" ? code : null),
                    "newPassword": (!newPassword ? null : newPassword)
                },
                "application": {
                    "code": this.props.location.query.client_id,
                    "redirectUrl": this.props.location.query.redirect_url
                },
                "authType": (type == "authcode" ? "T" : "P"),
                "nonce": this.props.location.query.state
            };
            if (this.props.location.query.scope) {
                // Scope is set:
                let scopeInfo = this.props.location.query.scope.split("=", 2);
                authRequest.activeRole = {
                    organization: {
                        clientId: scopeInfo[0]
                    },
                    role: {
                        clientId: scopeInfo[1]
                    }
                };
            }
            let action = new PostRestAction(authRequest);
            action.authToken = "";
            SessionActions.actionStart(action);
            // SessionActions.post(authRequest);
            // Remember last authentication type:
            SessionActions.setPasswordTab.defer(this.state.type);
        }.bind(this));
    }

    getScopeOrg() {
        if (!this.props.location.query.scope) return null;
        let scopeInfo = this.props.location.query.scope.split("=", 2);
        if (scopeInfo.length != 2) return null;
        return scopeInfo[0];
    }

    getScopeRole() {
        if (!this.props.location.query.scope) return null;
        let scopeInfo = this.props.location.query.scope.split("=", 2);
        if (scopeInfo.length != 2) return null;
        return scopeInfo[1];
    }

    onCancel() {
        // Remember last authentication type:
        SessionActions.setPasswordTab.defer(this.state.type);

        this.makeRedirect("");
    }

    isCurrentApp() {
        if (!this.props.location.query.redirect_url) return true;
        return this.props.location.query.redirect_url.startsWith("/login");
    }

    onForgotPassword() {
        this.showModal(<ForgotPassword onClose={this.onCloseForgotModal}/>);
    }

    showSelectRole() {
        this.showModal(<SelectRole onClose={this.onCloseRoleModal} isRequired={false} authToken={this.state.session.currentRecord.authToken}/>);
    }

    showChangePassword() {
        this.showModal(
            <ResetPassword
                type="session"
                onClose={this.onClosePasswordChange}
                clientId={this.props.location.query.client_id}
                redirectUrl={this.props.location.query.redirect_url}
                password={this.state.password}
                nonce={this.props.location.query.state}
                scope={this.props.location.query.scope} />);
    }

    onClosePasswordChange(passwordChanged) {
        this.showModal(null);
        if (passwordChanged) {
            if (this.roleSelectNeeded()) this.showSelectRole();
            else this.makeRedirect(this.state.session.currentRecord.authToken);
        }
        else this.makeRedirect("");
    }

    onCloseForgotModal(roleSelected) {
        this.showModal(null);
        this.makeRedirect("");
    }

    onCloseRoleModal(roleSelected) {
        this.showModal(null);
        if (roleSelected) this.makeRedirect(this.state.session.currentRecord.authToken);
        else this.makeRedirect("");
    }

    showModal(modalComponent) {
        this.setState({
            modal: modalComponent
        });
    }

    /**
     *
     * @param {string} key
     * @param {SessionState} newState
     */
    loadingFinished(key, newState) {
        if (key === "session" && newState.action.method === "post") {
            // If asked role matches current role, then we do not display role selection dialog:
            if (newState.currentRecord.status === "F") {
                // Password change requested:
                this.showChangePassword();
            }
            else if (this.roleSelectNeeded(newState)) this.showSelectRole();
            else this.makeRedirect(newState.currentRecord.authToken);
        }
    }

    /**
     *
     * @param {SessionState?} state
     */
    roleSelectNeeded(state) {
        if (!state) state = this.state.session;
        // If asked role matches current role, then we do not display role selection dialog:
        let needSelectRole = (state.currentRecord.person.personRoleCollection.items.length > 1);
        if (needSelectRole) {
            let askedOrg = this.getScopeOrg();
            let askedRole = this.getScopeRole();
            if (askedOrg && askedRole &&
                state.currentRecord.activeRole &&
                state.currentRecord.activeRole.organization.clientId === askedOrg &&
                state.currentRecord.activeRole.role.clientId === askedRole) needSelectRole = false;
        }
        return needSelectRole;
    }

    /**
     * Called when specified store fails to load data.
     * By default this method uses notification to display error.
     * @param key Store key
     * @param {BaseRestStoreState} newState Store new state
     * @param {BaseRestStoreState} oldState Store old state
     */
    loadingFailed(key, newState) {
        if (key === "session" && newState.action.method === "post") {
            switch (newState.action.error.reason) {
                case "auth:twoFactorRequired":
                    this.setState({
                        type: "authcode"
                    });
                    break;
                default:
                    break;
            }
            /*
            this.setState({
                type: "password"
            });*/
        }
        NotificationActions.actionError.defer(newState.action);
    }

    makeRedirect(token) {
        let url = this.props.location.query.redirect_url;
        if (url.indexOf("?") === -1) url += "?";
        else url += "&";
        let randomState = this.props.location.query.state;
        if (randomState == null) randomState = "";
        url += "code=" + encodeURIComponent(token) + "&state=" + encodeURIComponent(randomState);
        if (url.startsWith("/")) {
            let urlParts = url.split("?");
            let query = null;
            if (urlParts.length > 1) {
                query = queryParse(urlParts[1]);
            }
            Navigation.push({
                pathname: urlParts[0],
                query: query,
                state: {
                    redirect: (this.props.location.state ? this.props.location.state.redirect : null)
                }
            });
        }
        else Navigation.gotoExtUrl(url);
    }

    render() {
        return (
            <LoginPage inProgress={this.state.session.isLoading}
                       dialog={this.state.type}
                       email={this.state.username}
                       onLogin={this.onSubmitPassword}
                       onForgot={this.onForgotPassword}
                       onCancel={this.isCurrentApp() ? null : this.onCancel}>
                {this.state.modal}
            </LoginPage>
        );
    }
}

//AuthContainer.propTypes = propTypes;
//AuthContainer.defaultProps = defaultProps;

export default AuthContainer;
